import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Copy, Eye, Megaphone, Pin, SmilePlus, Users, Wifi, X } from 'lucide-react';
import liveChatApi from '../../../services/liveChatApi';
import { toast } from 'sonner';
import { useTenantTheme } from '../../../contexts/TenantThemeContext';
import SafeImage from '../../../components/SafeImage';

const EMOJI_REACTIONS = [
  '😀', '😃', '😄', '😁', '😂', '🤣', '😊', '😍', '🥰', '😘', '😎', '🥳',
  '🤩', '🤔', '🤭', '🫡', '😮', '😢', '😭', '😡', '🤯', '🥶', '🥵', '🙏',
  '👍', '👎', '👏', '🙌', '🤝', '💪', '🫶', '❤️', '🧡', '💛', '💚', '💙',
  '💜', '🖤', '🤍', '💔', '💕', '💖', '🔥', '💯', '✨', '⭐', '🌟', '⚡',
  '🎉', '🎊', '🎁', '🏆', '🥇', '💎', '🍀', '🌹', '🌈', '👀', '✅', '🚀',
];

const sampleFeed = {
  joined: false,
  member_count: 1248,
  online_count: 186,
  posts: [
    { id: 'demo-welcome', author_name: 'Community Admin', body: 'Welcome to the official community. Important updates, rewards, and announcements will be posted here.', pinned: true, created_at: new Date(Date.now() - 65 * 60000).toISOString(), view_count: 742, reactions: { '❤️': 68, '🔥': 22 }, my_reactions: [] },
    { id: 'demo-update', author_name: 'Community Admin', body: 'Today\'s bonus window is now open. Check Explore & Earn for your available rewards.', created_at: new Date(Date.now() - 20 * 60000).toISOString(), view_count: 431, reactions: { '👍': 35 }, my_reactions: [] },
  ],
};

const isLocalDemo = () => typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

const relativeTime = (value, now = Date.now()) => {
  const postedAt = new Date(value).getTime();
  if (!Number.isFinite(postedAt)) return '';
  const seconds = Math.max(0, Math.floor((now - postedAt) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} sec ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return `${hours} hr${remainingMinutes ? ` ${remainingMinutes} min` : ''} ago`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days} day${days === 1 ? '' : 's'}${remainingHours ? ` ${remainingHours} hr` : ''} ago`;
};

const absoluteTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const resolveMediaUrl = (value) => {
  const url = String(value || '').trim();
  if (!url || /^(data:|blob:|https?:)/i.test(url)) return url;
  return url.startsWith('/') ? url : `/${url}`;
};

const isInteractiveTarget = (target) => Boolean(target?.closest?.('button,a,input,textarea,img,[data-community-action]'));

const isSingleEmoji = (value) => {
  const cleanValue = String(value || '').trim();
  if (!cleanValue || cleanValue.length > 16) return false;
  const hasEmoji = /\p{Extended_Pictographic}|\p{Emoji_Presentation}|\p{Regional_Indicator}|\u20e3/u.test(cleanValue);
  if (!hasEmoji) return false;
  try {
    return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(cleanValue)].length === 1;
  } catch {
    return Array.from(cleanValue).length <= 8;
  }
};

export default function CommunityPanel({ onBack }) {
  const { theme } = useTenantTheme();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [activePostId, setActivePostId] = useState('');
  const [actionMode, setActionMode] = useState('menu');
  const [customEmoji, setCustomEmoji] = useState('');

  const tenantLogoUrl = resolveMediaUrl(theme?.logo_url || theme?.intro_logo_url || '');
  const tenantName = String(theme?.brand_name || '').trim();

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await liveChatApi.get('/api/chat/community');
      setFeed(data);
    } catch (error) {
      if (isLocalDemo()) setFeed(sampleFeed);
      else toast.error(error?.response?.data?.detail || 'Could not load the community');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFeed(); }, [loadFeed]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const join = async () => {
    setJoining(true);
    try {
      const { data } = await liveChatApi.post('/api/chat/community/join');
      setFeed(data);
      toast.success('You joined the community');
    } catch (error) {
      if (isLocalDemo()) {
        setFeed((current) => ({ ...current, joined: true, member_count: current.member_count + 1 }));
      } else toast.error(error?.response?.data?.detail || 'Could not join the community');
    } finally {
      setJoining(false);
    }
  };

  const react = async (postId, emoji) => {
    const cleanEmoji = String(emoji || '').trim();
    if (!feed?.joined || !cleanEmoji) return;
    if (!isSingleEmoji(cleanEmoji)) return toast.error('Please choose one emoji');
    try {
      const { data } = await liveChatApi.post(`/api/chat/community/posts/${postId}/react`, { emoji: cleanEmoji });
      setFeed(data);
      setActivePostId('');
      setActionMode('menu');
      setCustomEmoji('');
    } catch (error) {
      if (isLocalDemo()) {
        setFeed((current) => ({
          ...current,
          posts: current.posts.map((post) => post.id !== postId ? post : {
            ...post,
            reactions: { ...post.reactions, [cleanEmoji]: (post.reactions?.[cleanEmoji] || 0) + 1 },
            my_reactions: [...(post.my_reactions || []), cleanEmoji],
          }),
        }));
        closePostActions();
      } else toast.error(error?.response?.data?.detail || 'Reaction could not be saved');
    }
  };

  const openPostActions = (postId) => {
    setActivePostId(postId);
    setActionMode('menu');
    setCustomEmoji('');
  };

  const closePostActions = () => {
    setActivePostId('');
    setActionMode('menu');
    setCustomEmoji('');
  };

  const copyPost = async (post) => {
    const text = String(post?.body || '').trim();
    if (!text) return toast.info('This update only contains photos');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
    closePostActions();
    toast.success('Update copied');
  };

  const posts = feed?.posts || [];
  const activePost = useMemo(() => posts.find((post) => post.id === activePostId) || null, [activePostId, posts]);
  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-[#0e1621] text-white" data-testid="player-community-panel">
      <div className="relative z-10 flex shrink-0 items-center gap-3 border-b border-white/10 bg-[#17212b] px-4 pb-3 pt-[max(env(safe-area-inset-top),1rem)] shadow-lg">
        <button type="button" onClick={onBack} aria-label="Back to support" className="grid h-10 w-10 place-items-center rounded-full bg-white/8 text-white transition hover:bg-white/15"><ArrowLeft className="h-5 w-5" /></button>
        <div className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-[#31a8ff] to-[#7666ff] shadow-[0_0_22px_rgba(49,168,255,.4)]"><Megaphone className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[17px] font-black tracking-tight">Community</h2>
          <p className="flex items-center gap-1 text-xs text-[#8fabc1]"><Users className="h-3.5 w-3.5" /> {feed?.member_count ?? '—'} members <span className="mx-1 text-white/20">•</span><Wifi className="h-3.5 w-3.5 text-[#53d769]" /> {feed?.online_count ?? '—'} online</p>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 top-[74px] opacity-60" style={{ backgroundColor: '#0e1621', backgroundImage: 'radial-gradient(rgba(113,137,161,.17) 1px, transparent 1px), radial-gradient(rgba(113,137,161,.10) 1px, transparent 1px)', backgroundPosition: '0 0, 13px 13px', backgroundSize: '26px 26px' }} />
      <div className="relative z-[1] flex-1 overflow-y-auto px-3 py-4">
        {loading ? <div className="py-16 text-center text-sm text-[#8fabc1]">Loading community…</div> : !feed?.joined ? (
          <div className="mx-auto mt-10 max-w-sm rounded-[28px] border border-[#41a3ea]/35 bg-[#17212b]/95 p-6 text-center shadow-[0_20px_60px_rgba(0,0,0,.36)]">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#2aabee]/20 text-[#55bdf4]"><Megaphone className="h-8 w-8" /></div>
            <h3 className="mt-4 text-xl font-black">Join the community</h3>
            <p className="mt-2 text-sm leading-6 text-[#a8bdce]">Get official updates, reward announcements, and important news from the admin team.</p>
            <p className="mt-3 text-xs text-[#758fa5]">Only member and online totals are shown. Individual members stay private.</p>
            <button type="button" onClick={join} disabled={joining} className="mt-5 w-full rounded-xl bg-[#2aabee] px-4 py-3 text-sm font-black text-white shadow-[0_8px_25px_rgba(42,171,238,.32)] transition hover:bg-[#49baf1] disabled:opacity-60">{joining ? 'Joining…' : 'Join now'}</button>
          </div>
        ) : posts.length === 0 ? (
          <div className="mx-auto mt-14 max-w-sm rounded-2xl bg-[#17212b]/95 p-5 text-center text-sm text-[#a8bdce]">You’re in. The first admin update will appear here.</div>
        ) : (
          <div className="mx-auto max-w-xl space-y-3 pb-5">
            <div className="mx-auto w-fit rounded-full bg-[#182b38]/95 px-3 py-1 text-[11px] font-bold text-[#9ec4db]">Official tenant updates</div>
            {posts.map((post) => (
              <article
                key={post.id}
                role="button"
                tabIndex={0}
                aria-label="Open update actions"
                onClick={(event) => { if (!isInteractiveTarget(event.target)) openPostActions(post.id); }}
                onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openPostActions(post.id); } }}
                className="cursor-pointer rounded-2xl rounded-tl-md border border-[#294252] bg-[#1d2a35]/95 px-4 py-3 text-left shadow-[0_8px_18px_rgba(0,0,0,.18)] outline-none transition hover:border-[#3d6075] focus-visible:ring-2 focus-visible:ring-[#53bceb]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full border border-white/10 bg-gradient-to-br from-[#2aabee] to-[#7367f0] text-white shadow-sm">
                      <SafeImage src={tenantLogoUrl} alt={tenantName || 'Tenant'} className="h-full w-full object-cover" fallback={<Megaphone className="h-4 w-4" />} />
                    </div>
                    <div className="min-w-0"><p className="truncate text-sm font-black text-[#59bdf3]">{tenantName || post.author_name || 'Admin'}</p>{post.pinned && <p className="mt-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#e8c547]"><Pin className="h-3 w-3" /> Pinned update</p>}</div>
                  </div>
                  <time className="shrink-0 text-right text-[11px] leading-4 text-[#8ea9bd]" dateTime={post.created_at} title={absoluteTime(post.created_at)}>{relativeTime(post.created_at, now)}</time>
                </div>
                {post.body && <p className="mt-2 whitespace-pre-wrap text-[14px] leading-6 text-[#ecf5fb]">{post.body}</p>}
                {post.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {post.attachments.map((attachment) => (
                      <img key={attachment.id} src={attachment.url} alt={attachment.filename || 'Community update'} className="max-h-72 rounded-xl object-cover" loading="lazy" />
                    ))}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {Object.entries(post.reactions || {}).map(([emoji, count]) => <button key={emoji} type="button" onClick={() => react(post.id, emoji)} className={`rounded-full border px-2 py-1 text-xs transition ${post.my_reactions?.includes(emoji) ? 'border-[#53bceb] bg-[#2aabee]/20' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>{emoji} {count}</button>)}
                  <span className="ml-auto flex items-center gap-1 text-[11px] text-[#8ea9bd]"><Eye className="h-3.5 w-3.5" /> {post.view_count || 0}</span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {activePost && (
        <div className="absolute inset-0 z-50 flex items-end bg-black/55 backdrop-blur-[2px]" role="presentation" onClick={closePostActions}>
          <section
            role="dialog"
            aria-modal="true"
            aria-label="Community update actions"
            data-community-action
            onClick={(event) => event.stopPropagation()}
            className="w-full rounded-t-[26px] border border-b-0 border-white/10 bg-[#17212b] px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 shadow-[0_-18px_50px_rgba(0,0,0,.45)]"
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" />
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="min-w-0"><p className="truncate text-sm font-black text-white">{tenantName || activePost.author_name || 'Community update'}</p><p className="text-xs text-[#8ea9bd]">{relativeTime(activePost.created_at, now)} · {absoluteTime(activePost.created_at)}</p></div>
              <button type="button" onClick={closePostActions} aria-label="Close actions" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/8 text-white"><X className="h-4 w-4" /></button>
            </div>

            {actionMode === 'menu' ? (
              <div className="grid gap-2">
                <button type="button" onClick={() => setActionMode('react')} className="flex min-h-12 items-center gap-3 rounded-xl bg-white/7 px-4 text-left text-sm font-bold text-white hover:bg-white/12"><SmilePlus className="h-5 w-5 text-[#59bdf3]" /> React</button>
                <button type="button" onClick={() => copyPost(activePost)} className="flex min-h-12 items-center gap-3 rounded-xl bg-white/7 px-4 text-left text-sm font-bold text-white hover:bg-white/12"><Copy className="h-5 w-5 text-[#59bdf3]" /> Copy update</button>
              </div>
            ) : (
              <div>
                <div className="max-h-52 overflow-y-auto rounded-xl bg-[#0e1821] p-2">
                  <div className="grid grid-cols-8 gap-1.5 sm:grid-cols-10">
                    {EMOJI_REACTIONS.map((emoji) => (
                      <button key={emoji} type="button" onClick={() => react(activePost.id, emoji)} aria-label={`React ${emoji}`} className="grid aspect-square place-items-center rounded-lg text-xl transition hover:bg-white/10 active:scale-95">{emoji}</button>
                    ))}
                  </div>
                </div>
                <form className="mt-3 flex gap-2" onSubmit={(event) => { event.preventDefault(); react(activePost.id, customEmoji); }}>
                  <input value={customEmoji} onChange={(event) => setCustomEmoji(event.target.value)} maxLength={16} inputMode="text" autoFocus placeholder="Type or paste any emoji" className="min-h-11 min-w-0 flex-1 rounded-xl border border-white/10 bg-[#0e1821] px-3 text-base text-white outline-none placeholder:text-[#71899b] focus:border-[#53bceb]" />
                  <button type="submit" disabled={!customEmoji.trim()} className="min-h-11 rounded-xl bg-[#2aabee] px-4 text-sm font-black text-white disabled:opacity-45">React</button>
                </form>
                <button type="button" onClick={() => setActionMode('menu')} className="mt-2 w-full py-2 text-xs font-bold text-[#9fc5db]">Back to actions</button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
